import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiUsers,
  FiTrendingUp,
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiAlertCircle,
  FiDollarSign,
  FiBriefcase,
  FiPhone,
  FiMail,
  FiUserCheck,
  FiActivity,
  FiEye,
} from 'react-icons/fi';
import api from '../utils/api';
import Modal from '../components/ui/Modal';
import PasswordInput from '../components/ui/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const NIVELES_INTERES = {
  alto: { label: 'Alto', badge: 'badge-danger', icon: '🚨', desc: 'Máxima prioridad / Cierre próximo' },
  medio: { label: 'Medio', badge: 'badge-warning', icon: '⚡', desc: 'Interés normal / En seguimiento' },
  bajo: { label: 'Bajo', badge: 'badge-info', icon: '❄️', desc: 'Interés frío / Informativo' },
};

const ESTADOS_LEAD = {
  nuevo: { label: 'Nuevo', badge: 'badge-info' },
  contactado: { label: 'Contactado', badge: 'badge' },
  demostracion: { label: 'Demostración', badge: 'badge-warning' },
  negociando: { label: 'Negociando', badge: 'badge-warning' },
  vendido: { label: 'Vendido', badge: 'badge-ok' },
  descartado: { label: 'Descartado', badge: 'badge-danger' },
};

const VACIO = {
  nombre: '',
  email: '',
  telefono: '',
  empresa: '',
  interes: 'demo',
  nivelInteres: 'medio',
  estado: 'nuevo',
  valorEstimado: 0,
  notas: '',
  origen: 'manual',
  atendidoPor: '',
};

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export default function ClientesPotenciales() {
  const { usuario, empresa } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [landingSummary, setLandingSummary] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [notificacion, setNotificacion] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('reciente');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const [modal, setModal] = useState(false);
  const [modalConvertir, setModalConvertir] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [equipo, setEquipo] = useState([]);

  // Formulario de conversión
  const [formConvertir, setFormConvertir] = useState({
    nombreEmpresa: '',
    nombreAdmin: '',
    email: '',
    password: '',
    telefono: '',
    tipoNegocio: 'general',
    plan: 'starter',
  });
  const [convertiendo, setConvertiendo] = useState(false);

  const puedeCrear = puede(usuario, 'clientes', 'crear');
  const puedeEditar = puede(usuario, 'clientes', 'editar');
  const puedeEliminar = puede(usuario, 'clientes', 'eliminar');
  const esSuperadmin = usuario?.rol === 'superadmin';

  const mostrarNotificacion = (tipo, texto, duracion = 3200) => {
    setNotificacion({ tipo, texto });
    if (duracion > 0) {
      window.setTimeout(() => setNotificacion((actual) => (actual?.texto === texto ? null : actual)), duracion);
    }
  };

  const cargarDatos = async () => {
    setCargando(true);
    setError('');
    try {
      const [resLeads, resStats, resUsuarios, resLanding] = await Promise.all([
        api.get('/clientes-potenciales'),
        api.get('/clientes-potenciales/stats'),
        api.get('/usuarios').catch(() => ({ data: [] })),
        empresa?.landingPage?.activo
          ? api.get('/trafico/landing-summary', {
              params: {
                empresaId: empresa?._id,
                slug: empresa?.landingPage?.slug,
              },
            }).catch(() => ({ data: { activo: false, visitasUltimoMes: 0, clientesPotenciales: 0, conversion: 0, leadsRecientes: [] } }))
          : Promise.resolve({ data: { activo: false, visitasUltimoMes: 0, clientesPotenciales: 0, conversion: 0, leadsRecientes: [] } }),
      ]);

      setItems(Array.isArray(resLeads.data) ? resLeads.data : []);
      setStats(resStats.data || null);
      setLandingSummary(resLanding.data || { activo: false, visitasUltimoMes: 0, clientesPotenciales: 0, conversion: 0, leadsRecientes: [] });
      setEquipo(Array.isArray(resUsuarios.data) ? resUsuarios.data.filter((u) => u.rol !== 'superadmin') : []);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los clientes potenciales.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [empresa?._id, empresa?.landingPage?.activo, empresa?.landingPage?.slug]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      ...VACIO,
      atendidoPor: usuario?._id || usuario?.id || '',
    });
    setModal(true);
  };

  const abrirDetalle = (lead) => {
    setEditando(lead);
    setForm({
      nombre: lead.nombre || '',
      email: lead.email || '',
      telefono: lead.telefono || '',
      empresa: lead.empresa || '',
      interes: lead.interes || 'demo',
      nivelInteres: lead.nivelInteres || 'medio',
      estado: lead.estado || 'nuevo',
      valorEstimado: lead.valorEstimado || 0,
      notas: lead.notas || '',
      origen: lead.origen || 'manual',
      atendidoPor: lead.atendidoPor?._id || lead.atendidoPor || '',
    });
    setModal(true);
  };

  const setCampo = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        ...form,
        valorEstimado: Number(form.valorEstimado) || 0,
        atendidoPor: form.atendidoPor || null,
      };

      if (editando) {
        await api.put(`/clientes-potenciales/${editando._id}`, datos);
        mostrarNotificacion('success', 'Cliente potencial actualizado correctamente.');
      } else {
        await api.post('/clientes-potenciales', datos);
        mostrarNotificacion('success', 'Cliente potencial registrado correctamente.');
      }

      setModal(false);
      cargarDatos();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el cliente potencial.');
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (lead) => {
    if (!window.confirm(`¿Eliminar a "${lead.nombre || lead.empresa || lead.email}" de los clientes potenciales?`)) return;
    try {
      await api.delete(`/clientes-potenciales/${lead._id}`);
      mostrarNotificacion('success', 'Cliente potencial eliminado.');
      if (editando?._id === lead._id) setModal(false);
      cargarDatos();
    } catch {
      alert('No se pudo eliminar el cliente potencial.');
    }
  };

  const abrirConversion = (lead) => {
    setEditando(lead);
    setFormConvertir({
      nombreEmpresa: lead.empresa || lead.nombre || '',
      nombreAdmin: lead.nombre || '',
      email: lead.email || '',
      password: '',
      telefono: lead.telefono || '',
      tipoNegocio: 'general',
      plan: 'starter',
    });
    setModalConvertir(true);
  };

  const onEjecutarConversion = async (e) => {
    e.preventDefault();
    setConvertiendo(true);
    try {
      const { data } = await api.post(`/clientes-potenciales/${editando._id}/convertir`, formConvertir);
      mostrarNotificacion('success', data.mensaje || 'Cliente potencial convertido con éxito.');
      setModalConvertir(false);
      setModal(false);
      cargarDatos();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo convertir el cliente potencial.');
    } finally {
      setConvertiendo(false);
    }
  };

  const ORDEN_NIVEL_INTERES_VAL = { alto: 3, medio: 2, bajo: 1 };

  // Filtrado y ordenación (por defecto fecha de creación más reciente; alternativamente por nivel de interés)
  const filtrados = items
    .filter((item) => {
      const texto = [item.nombre, item.empresa, item.email, item.telefono, item.notas, item.interes]
        .join(' ')
        .toLowerCase();
      const textoOk = texto.includes(busqueda.toLowerCase());
      const nivelOk = !filtroNivel || item.nivelInteres === filtroNivel;
      const estadoOk = !filtroEstado || item.estado === filtroEstado;
      return textoOk && nivelOk && estadoOk;
    })
    .sort((a, b) => {
      if (orden === 'interes') {
        const diff = (ORDEN_NIVEL_INTERES_VAL[b.nivelInteres] || 2) - (ORDEN_NIVEL_INTERES_VAL[a.nivelInteres] || 2);
        if (diff !== 0) return diff;
      }
      return new Date(b.createdAt || b.ultimaActualizacion) - new Date(a.createdAt || a.ultimaActualizacion);
    });

  const nombreAtendido = (u) => {
    if (!u) return 'Sin asignar';
    if (typeof u === 'object') return `${u.nombre} ${u.apellidos || ''}`.trim() || u.email;
    const encontrado = equipo.find((x) => String(x._id) === String(u));
    return encontrado ? `${encontrado.nombre} ${encontrado.apellidos || ''}`.trim() : 'Asignado';
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Clientes Potenciales (Leads)</h1>
          <p className="page-subtitle">
            Gestión comercial de prospectos ordenada por fecha de creación o por nivel de interés
          </p>
        </div>
        {puedeCrear && (
          <button className="btn btn-primary" onClick={abrirNuevo}>
            <FiPlus /> Nuevo cliente potencial
          </button>
        )}
      </div>

      {notificacion && (
        <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>
          {notificacion.texto}
        </div>
      )}

      {/* DASHBOARD ÚTIL DE CLIENTES POTENCIALES */}
      {stats && (
        <div style={{ marginBottom: 24 }}>
          <div className="kpi-grid">
            <motion.div className="kpi-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                <FiTrendingUp />
              </div>
              <div className="kpi-body">
                <span className="kpi-value">{stats.alto}</span>
                <span className="kpi-label">Interés Alto (Calientes)</span>
                <span className="kpi-subtext">Atención prioritaria</span>
              </div>
            </motion.div>

            <motion.div className="kpi-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
              <div className="kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                <FiActivity />
              </div>
              <div className="kpi-body">
                <span className="kpi-value">{stats.enNegociacion}</span>
                <span className="kpi-label">En Negociación</span>
                <span className="kpi-subtext">{stats.nuevos} leads nuevos</span>
              </div>
            </motion.div>

            <motion.div className="kpi-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <div className="kpi-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                <FiDollarSign />
              </div>
              <div className="kpi-body">
                <span className="kpi-value">{fmtEuro(stats.totalValorEstimado)}</span>
                <span className="kpi-label">Valor total estimado</span>
                <span className="kpi-subtext">Pipeline potencial</span>
              </div>
            </motion.div>

            <motion.div className="kpi-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <div className="kpi-icon" style={{ background: '#f3e8ff', color: '#9333ea' }}>
                <FiCheckCircle />
              </div>
              <div className="kpi-body">
                <span className="kpi-value">{stats.vendidos} ({stats.tasaConversion}%)</span>
                <span className="kpi-label">Convertidos / Vendidos</span>
                <span className="kpi-subtext">De {stats.total} totales</span>
              </div>
            </motion.div>
          </div>

          {/* Indicador visual de distribución de interés */}
          <div className="table-wrap" style={{ marginTop: 16, padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', fontWeight: 600 }}>
              <FiAlertCircle style={{ color: '#3b82f6' }} /> Distribución por Interés:
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span className="badge badge-danger" style={{ fontSize: '0.85rem' }}>
                🚨 Alto: {stats.alto}
              </span>
              <span className="badge badge-warning" style={{ fontSize: '0.85rem' }}>
                ⚡ Medio: {stats.medio}
              </span>
              <span className="badge badge-info" style={{ fontSize: '0.85rem' }}>
                ❄️ Bajo: {stats.bajo}
              </span>
            </div>
          </div>
        </div>
      )}

      {empresa?.landingPage?.activo && (
        <div style={{ marginBottom: 24 }}>
          <div
            className="card"
            style={{
              padding: 0,
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 41, 59, 0.96) 100%)',
              border: '1px solid rgba(148, 163, 184, 0.18)',
              boxShadow: '0 22px 45px rgba(15, 23, 42, 0.18)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '20px 22px 12px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
              background: 'linear-gradient(90deg, rgba(37, 99, 235, 0.14), rgba(14, 165, 233, 0.05), rgba(15, 23, 42, 0.02))',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 38, height: 38, borderRadius: 12,
                  background: 'linear-gradient(135deg, #2563eb, #38bdf8)',
                  color: '#fff', fontSize: 18,
                  boxShadow: '0 10px 24px rgba(59, 130, 246, 0.35)',
                }}>
                  <FiTrendingUp />
                </div>
                <div>
                  <div style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#93c5fd', fontWeight: 700 }}>
                    Tráfico
                  </div>
                  <h3 style={{ margin: '4px 0 0', fontSize: 22, color: '#f8fafc' }}>Landing (últimos 30 días)</h3>
                </div>
              </div>
            </div>

            <div style={{ padding: 22 }}>
              <div className="kpi-grid" style={{ marginBottom: 18 }}>
                <div className="kpi-card" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(148,163,184,0.12)', boxShadow: 'none' }}>
                  <div className="kpi-icon" style={{ background: 'rgba(59,130,246,0.18)', color: '#93c5fd' }}>
                    <FiTrendingUp />
                  </div>
                  <div className="kpi-body">
                    <span className="kpi-value" style={{ color: '#f8fafc' }}>{landingSummary?.visitasUltimoMes ?? 0}</span>
                    <span className="kpi-label" style={{ color: '#e2e8f0' }}>Visitas</span>
                    <span className="kpi-subtext" style={{ color: '#cbd5e1' }}>Landing activa</span>
                  </div>
                </div>

                <div className="kpi-card" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(148,163,184,0.12)', boxShadow: 'none' }}>
                  <div className="kpi-icon" style={{ background: 'rgba(34,197,94,0.18)', color: '#86efac' }}>
                    <FiUsers />
                  </div>
                  <div className="kpi-body">
                    <span className="kpi-value" style={{ color: '#f8fafc' }}>{landingSummary?.clientesPotenciales ?? 0}</span>
                    <span className="kpi-label" style={{ color: '#e2e8f0' }}>Leads registrados</span>
                    <span className="kpi-subtext" style={{ color: '#cbd5e1' }}>Último mes</span>
                  </div>
                </div>

                <div className="kpi-card" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(148,163,184,0.12)', boxShadow: 'none' }}>
                  <div className="kpi-icon" style={{ background: 'rgba(245,158,11,0.18)', color: '#fcd34d' }}>
                    <FiActivity />
                  </div>
                  <div className="kpi-body">
                    <span className="kpi-value" style={{ color: '#f8fafc' }}>{landingSummary?.conversion ?? 0}%</span>
                    <span className="kpi-label" style={{ color: '#e2e8f0' }}>Conversión</span>
                    <span className="kpi-subtext" style={{ color: '#cbd5e1' }}>Visitas → leads</span>
                  </div>
                </div>
              </div>

              {(landingSummary?.leadsRecientes || []).length > 0 ? (
                <div className="table-wrap" style={{ background: 'rgba(15, 23, 42, 0.32)', borderRadius: 14, border: '1px solid rgba(148,163,184,0.12)' }}>
                  <table className="data-table" style={{ color: '#e2e8f0' }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#cbd5e1', background: 'rgba(15, 23, 42, 0.05)' }}>Nombre</th>
                        <th style={{ color: '#cbd5e1', background: 'rgba(15, 23, 42, 0.05)' }}>Email</th>
                        <th style={{ color: '#cbd5e1', background: 'rgba(15, 23, 42, 0.05)' }}>Empresa</th>
                        <th style={{ color: '#cbd5e1', background: 'rgba(15, 23, 42, 0.05)' }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(landingSummary?.leadsRecientes || []).map((lead) => (
                        <tr key={lead._id} style={{ background: 'transparent' }}>
                          <td data-label="Nombre" style={{ color: '#f8fafc' }}>{lead.nombre}</td>
                          <td data-label="Email" style={{ color: '#dbeafe' }}>{lead.email}</td>
                          <td data-label="Empresa" style={{ color: '#e2e8f0' }}>{lead.empresa || '—'}</td>
                          <td data-label="Fecha" style={{ color: '#cbd5e1' }}>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('es-ES') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="empty" style={{ margin: 0, color: '#cbd5e1' }}>No hay leads registrados desde la landing en los últimos 30 días.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BUSCADOR Y FILTROS */}
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <div className="search-box">
          <FiSearch />
          <input
            placeholder="Buscar por nombre, empresa, email, teléfono o notas..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <select className="mini-select" value={orden} onChange={(e) => setOrden(e.target.value)}>
          <option value="reciente">Orden: Más recientes primero (Por defecto)</option>
          <option value="interes">Orden: Mayor interés (Alto → Bajo)</option>
        </select>
        <select className="mini-select" value={filtroNivel} onChange={(e) => setFiltroNivel(e.target.value)}>
          <option value="">Todos los niveles de interés</option>
          <option value="alto">🚨 Interés Alto</option>
          <option value="medio">⚡ Interés Medio</option>
          <option value="bajo">❄️ Interés Bajo</option>
        </select>
        <select className="mini-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADOS_LEAD).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="alert-error">{error}</div>}

      {/* TABLA PRINCIPAL ORDENADA DE MAYOR A MENOR INTERÉS */}
      {cargando ? (
        <p className="empty">Cargando clientes potenciales…</p>
      ) : filtrados.length === 0 ? (
        <p className="empty"><FiUsers /> No hay clientes potenciales registrados.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Interés</th>
                <th>Cliente / Empresa</th>
                <th>Estado</th>
                <th>Contacto</th>
                <th>Atendido por</th>
                <th>Valor est.</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((item) => {
                const infoNivel = NIVELES_INTERES[item.nivelInteres] || NIVELES_INTERES.medio;
                const infoEstado = ESTADOS_LEAD[item.estado] || ESTADOS_LEAD.nuevo;

                return (
                  <tr
                    key={item._id}
                    onDoubleClick={() => abrirDetalle(item)}
                    style={{ cursor: 'pointer' }}
                    title="Haz doble clic para abrir la ficha completa y editar"
                  >
                    <td data-label="Interés">
                      <span className={`badge ${infoNivel.badge}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                        <span>{infoNivel.icon}</span> {infoNivel.label}
                      </span>
                    </td>
                    <td data-label="Cliente">
                      <strong>{item.nombre || 'Sin nombre'}</strong>
                      {item.empresa && <div className="sub" style={{ fontSize: '0.82rem' }}>🏢 {item.empresa}</div>}
                    </td>
                    <td data-label="Estado">
                      <span className={`badge ${infoEstado.badge}`}>{infoEstado.label}</span>
                    </td>
                    <td data-label="Contacto">
                      {item.telefono && (
                        <div>
                          <a href={`tel:${item.telefono}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: '0.85rem' }}>
                            <FiPhone style={{ verticalAlign: '-1px', marginRight: 3 }} /> {item.telefono}
                          </a>
                        </div>
                      )}
                      {item.email && (
                        <div>
                          <a href={`mailto:${item.email}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: '0.82rem', color: 'var(--text-soft)' }}>
                            <FiMail style={{ verticalAlign: '-1px', marginRight: 3 }} /> {item.email}
                          </a>
                        </div>
                      )}
                    </td>
                    <td data-label="Atendido por">
                      <span style={{ fontSize: '0.88rem' }}>
                        <FiUserCheck style={{ verticalAlign: '-1px', marginRight: 3 }} />
                        {nombreAtendido(item.atendidoPor)}
                      </span>
                    </td>
                    <td data-label="Valor est.">
                      {item.valorEstimado > 0 ? (
                        <strong style={{ color: '#16a34a' }}>{fmtEuro(item.valorEstimado)}</strong>
                      ) : '—'}
                    </td>
                    <td data-label="Fecha" style={{ fontSize: '0.82rem', color: 'var(--text-soft)' }}>
                      {new Date(item.createdAt || item.ultimaActualizacion).toLocaleDateString('es-ES')}
                    </td>
                    <td className="acciones" onClick={(e) => e.stopPropagation()}>
                      <button className="icon-btn" onClick={() => abrirDetalle(item)} title="Ver / Editar ficha del lead">
                        <FiEye />
                      </button>
                      {item.estado !== 'vendido' && (
                        <button className="btn mini btn-primary" onClick={() => abrirConversion(item)} title="Convertir lead">
                          Convertir
                        </button>
                      )}
                      {puedeEliminar && (
                        <button className="icon-btn danger" onClick={() => onEliminar(item)} title="Eliminar">
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL DE FICHA Y EDICIÓN COMPLETA DEL CLIENTE POTENCIAL */}
      <Modal
        abierto={modal}
        onCerrar={() => setModal(false)}
        titulo={editando ? `Cliente potencial: ${editando.nombre || editando.empresa || editando.email}` : 'Nuevo cliente potencial'}
        ancho={680}
      >
        <form onSubmit={onGuardar} className="form-grid">
          {/* INDICADOR DE INTERÉS DESTACADO */}
          <div className="span-2" style={{ background: 'var(--bg-card, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', padding: 14, borderRadius: 10, marginBottom: 6 }}>
            <label className="field" style={{ margin: 0 }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>🔥 Indicador de Interés *</span>
              <select value={form.nivelInteres} onChange={setCampo('nivelInteres')} style={{ fontWeight: 600 }}>
                <option value="alto">🚨 Alto — Máxima prioridad / Cierre próximo</option>
                <option value="medio">⚡ Medio — Interés normal / En seguimiento</option>
                <option value="bajo">❄️ Bajo — Interés frío / Informativo</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span>Estado del Lead *</span>
            <select value={form.estado} onChange={setCampo('estado')}>
              {Object.entries(ESTADOS_LEAD).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Atendido por / Asignado a</span>
            <select value={form.atendidoPor} onChange={setCampo('atendidoPor')}>
              <option value="">Sin asignar</option>
              {equipo.map((u) => (
                <option key={u._id} value={u._id}>{u.nombre} {u.apellidos}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Nombre de contacto</span>
            <input value={form.nombre} onChange={setCampo('nombre')} placeholder="Ej: Carlos Gómez" />
          </label>

          <label className="field">
            <span>Empresa / Negocio</span>
            <input value={form.empresa} onChange={setCampo('empresa')} placeholder="Ej: Taller Mecánico Gómez" />
          </label>

          <label className="field">
            <span>Email</span>
            <input type="email" value={form.email} onChange={setCampo('email')} placeholder="carlos@ejemplo.com" />
          </label>

          <label className="field">
            <span>Teléfono</span>
            <input value={form.telefono} onChange={setCampo('telefono')} placeholder="600112233" />
          </label>

          <label className="field">
            <span>Producto / Interés</span>
            <input value={form.interes} onChange={setCampo('interes')} placeholder="Ej: demo, taller, facturación" />
          </label>

          <label className="field">
            <span>Valor Estimado (€)</span>
            <input type="number" min="0" step="100" value={form.valorEstimado} onChange={setCampo('valorEstimado')} />
          </label>

          <label className="field span-2">
            <span>Notas de seguimiento y comentarios</span>
            <textarea rows={4} value={form.notas} onChange={setCampo('notas')} placeholder="Añade aquí las notas de llamadas, acuerdos, necesidades del cliente..." />
          </label>

          <div className="form-actions span-2" style={{ justifyContent: 'space-between', marginTop: 10 }}>
            <div>
              {editando && editando.estado !== 'vendido' && (
                <button type="button" className="btn btn-primary" onClick={() => abrirConversion(editando)}>
                  Convertir a Cliente
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar datos'}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* MODAL DE CONVERSIÓN A EMPRESA / CLIENTE */}
      <Modal abierto={modalConvertir} onCerrar={() => setModalConvertir(false)} titulo="Convertir Cliente Potencial" ancho={540}>
        <form onSubmit={onEjecutarConversion} className="form-grid">
          <p className="muted span-2">
            Al convertir este lead, se cambiará su estado a <strong>Vendido</strong>.
            {esSuperadmin && ' Se creará una nueva empresa activa en Trix con su administrador correspondiente.'}
          </p>

          {esSuperadmin ? (
            <>
              <label className="field span-2">
                <span>Nombre de la empresa *</span>
                <input required value={formConvertir.nombreEmpresa} onChange={(e) => setFormConvertir((f) => ({ ...f, nombreEmpresa: e.target.value }))} />
              </label>
              <label className="field">
                <span>Nombre del administrador *</span>
                <input required value={formConvertir.nombreAdmin} onChange={(e) => setFormConvertir((f) => ({ ...f, nombreAdmin: e.target.value }))} />
              </label>
              <label className="field">
                <span>Email de acceso *</span>
                <input required type="email" value={formConvertir.email} onChange={(e) => setFormConvertir((f) => ({ ...f, email: e.target.value }))} />
              </label>
              <label className="field span-2">
                <span>Contraseña inicial *</span>
                <PasswordInput required value={formConvertir.password} onChange={(e) => setFormConvertir((f) => ({ ...f, password: e.target.value }))} />
              </label>
            </>
          ) : (
            <div className="alert-info span-2">
              El cliente potencial "{editando?.nombre || editando?.empresa}" pasará a estado <strong>Vendido</strong>.
            </div>
          )}

          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModalConvertir(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={convertiendo}>
              {convertiendo ? 'Convertiendo…' : 'Confirmar conversión'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}